import { create } from 'zustand';
import type { LookDto, LookInput, ProductDetailDto, ProductSummaryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';

/**
 * Looks (R17): products that go together, each with how much. A save answers
 * with the whole look, priced, so the list shows what the shop window will.
 *
 * The editor is a page of its own (`/looks/:id`), so the store holds the open
 * look beside the list, and the calls the editor's product picker makes —
 * a page of products, one product's fabrics, a photograph's upload — go
 * through here too rather than from the page (API → Store → Render).
 */

interface LooksState {
  items: LookDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  saving: boolean;
  error: string | null;
  search: string;

  current: LookDto | null;
  currentLoading: boolean;

  load: (search?: string) => Promise<void>;
  loadMore: () => Promise<void>;
  open: (id: string) => Promise<void>;
  clearCurrent: () => void;
  /** The saved look, priced — or null, with the reason in `error`. */
  save: (id: string | null, input: LookInput) => Promise<LookDto | null>;
  remove: (id: string) => Promise<boolean>;

  /** A page of products for the picker; a failure is an empty page, not a broken sheet. */
  searchProducts: (search: string, cursor?: string) => Promise<{ items: ProductSummaryDto[]; nextCursor: string | null }>;
  /** One product with its variants, images and colour labels — its fabrics. */
  loadProduct: (id: string) => Promise<ProductDetailDto | null>;
  /** Uploads a photograph and answers with its storage key and a URL to preview it (ADR-013). */
  uploadImage: (file: File) => Promise<{ key: string; url: string } | null>;
}

const PAGE_SIZE = 20;
const PICKER_PAGE_SIZE = 24;
const locale = () => useUiStore.getState().locale;

export const useLooksStore = create<LooksState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  saving: false,
  error: null,
  search: '',

  current: null,
  currentLoading: false,

  async load(search) {
    const next = search ?? get().search;
    set({ loading: true, error: null, search: next });
    try {
      const page = await withSession(() => adminApi.looks.list({ limit: PAGE_SIZE, search: next || undefined, locale: locale() }));
      set({ items: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the looks.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, search, items } = get();
    if (!nextCursor || loadingMore) return;
    set({ loadingMore: true });
    try {
      const page = await withSession(() =>
        adminApi.looks.list({ limit: PAGE_SIZE, cursor: nextCursor, search: search || undefined, locale: locale() }),
      );
      set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more looks.') });
    }
  },

  async open(id) {
    set({ currentLoading: true, error: null });
    try {
      const look = await withSession(() => adminApi.looks.get(id, locale()));
      set({ current: look, currentLoading: false });
    } catch (error) {
      set({ currentLoading: false, error: errorMessage(error, 'Could not load this look.') });
    }
  },

  clearCurrent() {
    set({ current: null, error: null });
  },

  async save(id, input) {
    set({ saving: true, error: null });
    try {
      const look = await withSession(() => (id ? adminApi.looks.update(id, input, locale()) : adminApi.looks.create(input, locale())));
      set((state) => ({
        saving: false,
        current: look,
        // The list shows what was saved without a refetch; a new look joins at the top.
        items: state.items.some((entry) => entry.id === look.id)
          ? state.items.map((entry) => (entry.id === look.id ? look : entry))
          : [look, ...state.items],
      }));
      return look;
    } catch (error) {
      // "A look is added without questions" comes back from the API in words.
      set({ saving: false, error: errorMessage(error, 'Could not save the look.') });
      return null;
    }
  },

  async remove(id) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.looks.remove(id));
      set((state) => ({ saving: false, items: state.items.filter((look) => look.id !== id) }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not delete the look.') });
      return false;
    }
  },

  async searchProducts(search, cursor) {
    try {
      // Only what is on sale: a look hides while any of its products is not (Looks.md).
      const params = { limit: PICKER_PAGE_SIZE, cursor, search: search || undefined, status: 'ACTIVE' };
      const page = await withSession(() => adminApi.catalog.products(params));
      return { items: page.items, nextCursor: page.meta.nextCursor };
    } catch {
      return { items: [], nextCursor: null };
    }
  },

  async loadProduct(id) {
    try {
      return await withSession(() => adminApi.catalog.product(id));
    } catch {
      return null;
    }
  },

  async uploadImage(file) {
    try {
      const uploaded = await withSession(() => adminApi.uploads.image(file));
      return { key: uploaded.key, url: uploaded.url };
    } catch {
      return null;
    }
  },
}));
